import React, { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { LoadingState } from '../components/common/LoadingState';

/**
 * Login page component
 * Maps to task requirement: auth flow
 * WCAG 2.1 AA compliant with proper form labels and error handling
 *
 * SINGLE ENTRY POINT BY DESIGN.
 *
 * This page used to offer two competing ways in - an "Sign in with SSO"
 * button AND its own email/password form - which read as two different
 * accounts to users rather than two routes to the same one.
 *
 * All three authentication methods are still fully supported; the choice
 * simply moved one step later, to Keycloak's own hosted page, which already
 * presents BOTH (verified live against the finops realm):
 *   1. email + password  - Keycloak's own login form
 *   2. Microsoft         - the `microsoft` identity provider on the realm
 *   3. Keycloak SSO      - this redirect itself (Authorization Code + PKCE)
 *
 * authService.login() (the Direct Access Grant / ROPC path) is deliberately
 * RETAINED in the codebase and still works - it is just no longer surfaced
 * here. Do not delete it.
 *
 * Side benefit worth knowing: the hosted page runs the Authorization Code
 * flow, which CAN complete Keycloak required actions (CONFIGURE_TOTP,
 * UPDATE_PASSWORD, VERIFY_PROFILE). ROPC cannot - which is why accounts
 * carrying a pending required action were previously locked out permanently
 * with a misleading "Invalid email or password". Routing everyone through
 * this button makes that class of lockout self-recoverable, and is the
 * prerequisite for ever re-enabling MFA on invites.
 */
export const LoginPage: React.FC = () => {
  const { loginWithSSO, isLoading, error, clearError } = useAuthStore();
  const { darkMode, toggleDarkMode } = useThemeStore();

  // The Header component normally owns syncing the `dark` class onto
  // <html>, but Header isn't mounted on the login route - so this page
  // needs its own copy of that sync, or the toggle below would do nothing.
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const handleSSOClick = async () => {
    clearError();
    try {
      await loginWithSSO(); // navigates the browser away to Keycloak's login page
    } catch (err) {
      // Error is handled by the store
    }
  };

  if (isLoading) {
    return <LoadingState message="Signing in..." />;
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-b from-gray-100 to-muted/30 px-4 py-12 dark:from-gray-950 dark:to-gray-900 sm:px-6 lg:px-8">
      <button
        onClick={toggleDarkMode}
        className="absolute right-4 top-4 rounded-md p-2 text-muted-foreground hover:bg-secondary/60 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-muted-foreground/70 dark:hover:bg-secondary/60 dark:hover:text-white"
        aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {darkMode ? (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ) : (
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
        )}
      </button>

      <div className="w-full max-w-md space-y-8 rounded-2xl border border-border bg-card p-8 shadow-xl dark:border-border dark:bg-card">
        <div>
          <img
            src="/apexon_logo-removebg-preview.png"
            alt="Apexon"
            className="mx-auto h-16 w-16 object-contain"
          />
          <h1 className="mt-4 text-center text-2xl font-bold tracking-tight text-foreground">
            CloudAlpha Agentic Fin-Ops
          </h1>
          <p className="mt-1 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
            by Apexon
          </p>
          <h2 className="mt-6 text-center text-xl font-semibold text-foreground/80">
            Sign in to your account
          </h2>
        </div>

        {/* SSO can still fail (PKCE verifier lost, Keycloak unreachable), so the
            error region stays - it is just no longer attached to a form. */}
        {error && (
          <div
            className="rounded-md bg-rose-50 p-4 dark:bg-rose-900/20"
            role="alert"
            aria-live="assertive"
          >
            <p className="text-sm text-rose-800 dark:text-rose-200">{error}</p>
          </div>
        )}

        {/* No mt-* here: the card is `space-y-8`, which already spaces its
            direct children - the old layout stacked both and over-gapped. */}
        <div>
          {/* Promoted to the solid primary style: it is now the ONLY action on
              this page, so rendering it as a secondary/outline button next to
              nothing would misrepresent it as the lesser of two options. */}
          <button
            type="button"
            onClick={handleSSOClick}
            disabled={isLoading}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-transparent bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Sign in with SSO"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 2L2 7v10l10 5 10-5V7l-10-5z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
              <path d="M12 12l10-5M12 12v10M12 12L2 7" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
            Sign in with SSO
          </button>

          {/* Sets the expectation for the next screen. Without it, landing on a
              Keycloak-branded page that offers BOTH a Microsoft button and a
              password form reads as a redirect gone wrong rather than as the
              intended single front door. */}
          <p className="mt-4 text-center text-xs leading-relaxed text-muted-foreground">
            You&rsquo;ll continue on your organisation&rsquo;s secure sign-in page, where you can
            use your Microsoft work account or your email and password.
          </p>

          {/* Self-service password reset is deliberately NOT enabled on the
              realm: this is an invite-only platform holding financial data, so
              an email-based reset would be a standing account-takeover route -
              and with SMTP unavailable it could not deliver anyway.
              Resets are performed by an administrator in Admin Console.
              That is a sound policy, but it left a genuine dead end: a locked
              out non-admin cannot reach the account menu (not signed in) OR
              Admin Console (not an admin), and nothing on screen told them who
              to ask. This line is the only route out for that user. */}
          <p className="mt-6 border-t border-border pt-4 text-center text-xs text-muted-foreground">
            Forgotten your password or locked out?{' '}
            <span className="font-medium text-foreground/80">
              Contact your CloudAlpha administrator
            </span>{' '}
            to have it reset.
          </p>
        </div>
      </div>
    </div>
  );
};
